import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@/components/ui';
import { useT } from '@/lib/i18n';

import { fetchDish, formatPrice, type DishDetailDto, type DishSummaryDto } from './api';

export function DishPane({
  shopSlug,
  dish,
  language,
}: {
  shopSlug: string;
  dish: DishSummaryDto;
  language: string;
}) {
  const t = useT();
  const [detail, setDetail] = useState<DishDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const cache = useRef(new Map<string, DishDetailDto>());

  useEffect(() => {
    const cached = cache.current.get(dish.id);
    if (cached) {
      setDetail(cached);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    fetchDish(shopSlug, dish.id, language, controller.signal)
      .then((d) => {
        cache.current.set(dish.id, d);
        setDetail(d);
      })
      .catch(() => {
        if (!controller.signal.aborted) setDetail(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [shopSlug, dish.id, language]);

  const price = formatPrice(
    dish.price_amount,
    dish.currency,
    dish.tax_included,
    t('menu.taxIncluded'),
    t('menu.taxExcluded'),
  );
  const optionHref = `/shop/${shopSlug}/option/${dish.id}${language ? `?lang=${language}` : ''}`;
  const qaHref = `/shop/${shopSlug}/ask${language ? `?lang=${language}` : ''}`;

  const subName = detail?.name_ja ?? null;

  return (
    <div
      className="flex min-w-0 flex-col gap-[10px] transition-opacity"
      style={loading && !detail ? { opacity: 0.6 } : undefined}
    >
      
      <div className="relative h-[132px] overflow-hidden rounded-[14px] bg-ink-100 sm:h-[180px] lg:h-[240px] xl:h-[280px]">
        {detail?.image_url ? (
          <img
            src={detail.image_url}
            alt={dish.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-plum-300">
            <Icon name="image" size={28} />
          </div>
        )}

        {price ? (
          <span
            className="absolute top-[9px] left-[9px] rounded-[8px] px-[11px] py-[5px] text-[16px] font-bold text-plum-700"
            style={{
              background: 'rgba(255,255,255,.95)',
              boxShadow: 'var(--shadow-sm)',
              backdropFilter: 'blur(4px)',
            }}
          >
            {price}
          </span>
        ) : null}

        
        <Link
          to={optionHref}
          className="absolute top-[9px] right-[9px] rounded-[8px] px-[13px] py-[6px] text-[12.5px] font-semibold text-white no-underline transition-colors hover:brightness-95"

          style={{ background: 'rgba(77,119,64,.97)' }}
        >
          {dish.option_text ?? t('dish.option')}
        </Link>
      </div>

      
      
      <h2 className="text-[15px] leading-tight font-bold tracking-[-.01em] text-ink-900">
        {dish.name}
        {dish.fallback_applied ? (
          <span className="ml-2 text-[12px] font-normal text-amber-600">{t('common.notTranslated')}</span>
        ) : null}
      </h2>
      {subName ? <p className="-mt-[6px] text-[12px] text-plum-600">{subName}</p> : null}

      
      <PaneCard title={t('dish.description')} body={detail?.description ?? null} />
      <PaneCard title={t('dish.materials')} body={detail?.ingredients_text ?? null} />
      
      
      <PaneCard
        title={t('dish.rating')}
        body={detail?.ingredients_text ?? null}
        {...(detail?.rating_text ? { tag: detail.rating_text } : {})}
      />

      
      <Link
        to={qaHref}
        className="group flex items-center justify-center gap-2 rounded-[11px] border border-plum-200 px-3.5 py-2.5 text-[13px] font-semibold text-plum-800 no-underline transition-all hover:-translate-y-px hover:border-plum-300 hover:shadow-[0_6px_16px_-10px_rgb(89_58_97/0.5)]"
        style={{
          background:
            'linear-gradient(180deg, var(--color-plum-100) 0%, color-mix(in oklab, var(--color-plum-100) 70%, white) 100%)',
        }}
      >
        <Icon name="sparkle" size={15} />
        {t('dish.askAboutThisDish')}
      </Link>
    </div>
  );
}

function PaneCard({
  title,
  body,
  tag,
}: {
  title: string;
  body: string | null;
  tag?: string;
}) {
  if (!body) return null;

  return (
    <div className="rounded-[10px] border border-line bg-paper px-[14px] py-[13px] transition-colors hover:border-ink-300">
      <div className="mb-[7px] flex items-center gap-2">
        <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-ink-900" aria-hidden="true" />
        <span className="text-[13px] font-bold text-ink-900">{title}</span>
        {tag ? (
          <span
            className="ml-auto rounded-full px-[9px] py-[2px] text-[11px] font-semibold text-amber-700"
            style={{ background: 'var(--color-amber-100)' }}
          >
            {tag}
          </span>
        ) : null}
      </div>
      <p className="text-[12.5px] leading-[1.55] text-ink-700">{body}</p>
    </div>
  );
}
